import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles, ListChecks, Layers, CalendarCheck, LineChart, ShieldCheck, ArrowRight, Clock, MonitorPlay, ExternalLink, Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { AppearanceMenu } from "@/components/AppearanceMenu";
import { Logo } from "@/components/Logo";
import { LegalLinks } from "@/components/LegalLinks";
import { SubjectCard } from "@/components/SubjectCard";
import SampleQuiz from "@/components/SampleQuiz";
import EnquiryDialog from "@/components/EnquiryDialog";
import DomainDialog from "@/components/DomainDialog";
import { Loading } from "@/components/States";
import { api, fetchSubjects } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const HERO_IMG = "https://images.unsplash.com/photo-1582719471384-894fbb16e074?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200";

const BENEFITS = [
  [Sparkles, "AI Learning Assistant", "Structured explanations with definitions, examples, key takeaways and an honest confidence note."],
  [ListChecks, "Validated quizzes", "5, 10 or 20 MCQs where every answer key is checked before you see it, with instant explanations."],
  [Layers, "Smart flashcards", "Generate decks in seconds, mark cards as known or for revision, and revisit any time."],
  [CalendarCheck, "Daily quiz & streaks", "A quick daily set from the expert-approved question bank to keep your knowledge sharp."],
  [LineChart, "Progress insights", "Subject-wise accuracy and weak-topic detection that tells you exactly what to revise next."],
  [ShieldCheck, "Built for industry", "Aligned with ICH, CDISC and real job roles across CDM, PV, Biostatistics and SDTM."],
];

const NAV = [["#subjects", "Domains", "domains"], ["#sample", "Practice", "practice"], ["#features", "Platform", "platform"], ["#programs", "Programs", "programs"], ["/pricing", "Plans", "pricing"], ["/talent", "Talent", "talent"]];

const NavLinkItem = ({ to, label, id, className, onClick }) => (to.startsWith("#")
  ? <a href={to} data-testid={`landing-${id}-link`} onClick={onClick} className={className}>{label}</a>
  : <Link to={to} data-testid={`landing-${id}-link`} onClick={onClick} className={className}>{label}</Link>);

function MobileNav({ user }) {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button data-testid="landing-mobile-menu-btn" aria-label="Open menu" className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-line text-ink-2 transition-colors hover:text-ink md:hidden"><Menu className="h-4 w-4" /></button>
      </SheetTrigger>
      <SheetContent side="right" data-testid="landing-mobile-menu" className="w-72 border-line bg-canvas-2 p-6 pt-14">
        <nav className="flex flex-col gap-1">
          {NAV.map(([to, label, id]) => <NavLinkItem key={id} to={to} label={label} id={`mobile-${id}`} onClick={close} className="rounded-lg px-3 py-3 font-display text-base font-semibold text-ink transition-colors hover:bg-elevated" />)}
        </nav>
        {!user && <div className="mt-6 flex flex-col gap-2 border-t border-line pt-6">
          <Button asChild variant="outline" className="rounded-full"><Link data-testid="mobile-login-btn" to="/login" onClick={close}>Log in</Link></Button>
          <Button asChild className="rounded-full"><Link data-testid="mobile-register-btn" to="/register" onClick={close}>Get started</Link></Button>
        </div>}
      </SheetContent>
    </Sheet>
  );
}

function PublicNav() {
  const { user } = useAuth();
  return (
    <header className="sticky top-0 z-40 border-b border-line/80 bg-canvas/85 backdrop-blur-md">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
        <Logo className="h-9 sm:h-10" />
        <nav data-testid="landing-nav" className="hidden items-center gap-1 text-sm font-medium tracking-wide text-ink-2 md:flex lg:gap-2">
          {NAV.map(([to, label, id]) => <NavLinkItem key={id} to={to} label={label} id={id} className="rounded-full px-3 py-2 transition-colors hover:bg-elevated/70 hover:text-ink" />)}
        </nav>
        <div className="flex items-center gap-2">
          <AppearanceMenu />
          {user ? (
            <Button asChild className="rounded-full"><Link data-testid="nav-dashboard-btn" to="/dashboard">Go to dashboard</Link></Button>
          ) : (
            <>
              <Button asChild variant="ghost" className="hidden rounded-full sm:inline-flex"><Link data-testid="nav-login-btn" to="/login">Log in</Link></Button>
              <Button asChild className="rounded-full"><Link data-testid="nav-register-btn" to="/register">Get started</Link></Button>
            </>
          )}
          <MobileNav user={user} />
        </div>
      </div>
    </header>
  );
}

function Hero({ subjects }) {
  return (
    <section className="relative overflow-hidden">
      <div className="grid-bg absolute inset-0 [mask-image:linear-gradient(to_bottom,black,transparent)]" />
      <div className="relative mx-auto grid max-w-7xl gap-12 px-4 pb-20 pt-16 sm:px-6 lg:grid-cols-12 lg:px-8 lg:pt-24">
        <div className="lg:col-span-7 fade-up">
          <span className="inline-flex items-center gap-2 rounded-full border border-aqua/30 bg-aqua-soft px-3 py-1 text-xs font-semibold text-aqua">
            <Sparkles className="h-3.5 w-3.5" /> AI-Powered Learning and Quiz Generator
          </span>
          <h1 className="mt-6 text-4xl font-extrabold leading-[1.05] tracking-tight text-ink sm:text-5xl lg:text-6xl">
            Master clinical research, <span className="text-aqua">one validated question</span> at a time.
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-ink-2 sm:text-lg">
            IDDCR LearnAI helps students, graduates and working professionals master the full clinical research lifecycle, from trial
            fundamentals, operations and regulation to data management, safety, biostatistics, CDISC standards, programming and AI, with instant explanations, practice quizzes, flashcards and progress tracking.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild size="lg" className="rounded-full px-7"><Link data-testid="hero-register-btn" to="/register">Start learning free <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
            <Button asChild size="lg" variant="outline" className="rounded-full px-7"><a data-testid="hero-sample-btn" href="#sample">Try a sample quiz</a></Button>
          </div>
          <dl className="mt-12 grid max-w-lg grid-cols-3 gap-6 border-t border-line pt-6">
            {[[subjects ? subjects.length : "—", "Learning domains"], [subjects ? subjects.reduce((n, x) => n + (x.topics?.length || 0), 0) : "—", "Curated topics"], ["3", "Difficulty levels"]].map(([n, l]) => (
              <div key={l}><dt className="font-display text-3xl font-extrabold text-ink">{n}</dt><dd className="text-xs text-ink-3">{l}</dd></div>
            ))}
          </dl>
        </div>
        <div className="relative lg:col-span-5 fade-up" style={{ animationDelay: "120ms" }}>
          <div className="absolute -right-6 -top-6 h-40 w-40 rounded-2xl bg-brand" />
          <div className="absolute -bottom-6 -left-6 h-24 w-24 rounded-2xl bg-[var(--brand-cyan)]" />
          <img src={HERO_IMG} alt="Clinical research laboratory" className="relative aspect-[4/5] w-full rounded-2xl object-cover shadow-2xl" />
          <div className="absolute bottom-6 left-6 right-6 rounded-xl border border-white/60 bg-surface/90 p-4 backdrop-blur-md">
            <p className="text-xs font-semibold uppercase tracking-wider text-aqua">Today's daily quiz</p>
            <p className="mt-1 text-sm font-semibold text-ink">5 approved questions · 3 min · Keep your streak alive</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Programs() {
  const [courses, setCourses] = useState(null);
  const [enquire, setEnquire] = useState(null);
  const { user } = useAuth();
  const nav = useNavigate();
  const openEnquiry = (c) => (user ? setEnquire(c) : nav("/login", { state: { from: "/#programs" } }));
  useEffect(() => { api.get("/courses").then((r) => setCourses(r.data)).catch(() => setCourses([])); }, []);
  return (
    <section id="programs" className="bg-band py-20 text-white lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--brand-cyan)]">IDDCR Global Institute</p>
        <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <h2 className="mt-3 max-w-2xl text-3xl font-extrabold tracking-tight sm:text-4xl">Ready to go deeper? Explore industry training programs.</h2>
          <div className="flex flex-wrap gap-3 text-sm">
            <a data-testid="programs-catalogue-link" href="https://www.iddcrinstitute.com/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full bg-surface px-4 py-2 font-semibold text-ink hover:bg-elevated">Full catalogue <ExternalLink className="h-3.5 w-3.5" /></a>
            <a data-testid="programs-email-link" href="mailto:info@iddcrinstitute.com" className="inline-flex items-center rounded-full border border-white/30 px-4 py-2 font-semibold text-white hover:bg-white/10">info@iddcrinstitute.com</a>
          </div>
        </div>
        {!courses ? <Loading /> : (
          <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {courses.map((c, i) => (
              <div key={c.id} data-testid={`course-card-${i}`} className="flex flex-col rounded-2xl border border-white/10 bg-white/[0.04] p-6 transition-colors hover:bg-white/[0.08]">
                <h3 className="text-lg font-bold">{c.title}</h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-300">{c.description}</p>
                <div className="mt-5 flex flex-wrap gap-4 text-xs text-slate-400">
                  {c.duration && <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />{c.duration}</span>}
                  {c.mode && <span className="flex items-center gap-1.5"><MonitorPlay className="h-3.5 w-3.5" />{c.mode}</span>}
                </div>
                <div className="mt-5 flex flex-wrap items-center gap-4">
                  <button data-testid={`course-enquiry-btn-${i}`} onClick={() => openEnquiry(c)}
                    className="rounded-full bg-aqua-solid px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-aqua-solid-hover">Enquire</button>
                  {c.enquiry_url && (
                    <a data-testid={`course-enquire-${i}`} href={c.enquiry_url} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--brand-cyan)] hover:underline">
                      View details & enrol <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        <EnquiryDialog course={enquire} onClose={() => setEnquire(null)} />
      </div>
    </section>
  );
}

export default function Landing() {
  const [subjects, setSubjects] = useState(null);
  const [domain, setDomain] = useState(null);
  useEffect(() => { fetchSubjects().then(setSubjects).catch(() => setSubjects([])); }, []);
  return (
    <div className="bg-canvas">
      <PublicNav />
      <Hero subjects={subjects} />
      <section id="subjects" className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
        <div className="max-w-2xl">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-aqua">What you'll learn</p>
          <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">{subjects?.length || "Ten"} domains across the clinical research lifecycle. Every level from beginner to advanced.</h2>
        </div>
        {!subjects ? <Loading /> : (
          <div data-testid="domains-grid" className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {subjects.map((s, i) => (
              <div key={s.id} className={s.primary ? "sm:col-span-2 lg:col-span-3" : ""}>
                <SubjectCard subject={s} index={i} showTopics onClick={() => setDomain(s)} />
              </div>
            ))}
          </div>
        )}
      </section>
      <section id="sample" className="border-y border-line bg-canvas-2/70 py-20 lg:py-24">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
          <div className="lg:col-span-5">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-aqua">No sign-up needed</p>
            <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">Try a 3-question sample quiz.</h2>
            <p className="mt-4 text-ink-2">Questions come from our expert-approved bank. Pick an answer to get instant feedback and an explanation — just like the full quiz experience.</p>
          </div>
          <div className="lg:col-span-7"><SampleQuiz /></div>
        </div>
      </section>
      <section id="features" className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
        <h2 className="max-w-2xl text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">Everything you need to learn, practise and prepare for interviews.</h2>
        <div className="mt-12 grid grid-cols-1 gap-x-10 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
          {BENEFITS.map(([Icon, t, d]) => (
            <div key={t} className="border-l-2 border-aqua pl-5">
              <Icon className="h-5 w-5 text-aqua" />
              <h3 className="mt-3 text-lg font-bold text-ink">{t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-ink-2">{d}</p>
            </div>
          ))}
        </div>
      </section>
      <Programs />
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="flex flex-col items-start justify-between gap-6 rounded-3xl border border-line p-8 sm:p-12 md:flex-row md:items-center">
          <div>
            <h2 className="text-2xl font-extrabold text-ink sm:text-3xl">Start your clinical research journey today.</h2>
            <p className="mt-2 text-ink-2">Free account. Track progress across every subject.</p>
          </div>
          <div className="flex gap-3">
            <Button asChild size="lg" className="rounded-full"><Link data-testid="cta-register-btn" to="/register">Create account</Link></Button>
            <Button asChild size="lg" variant="outline" className="rounded-full"><Link data-testid="cta-login-btn" to="/login">Log in</Link></Button>
          </div>
        </div>
      </section>
      <DomainDialog subject={domain} onClose={() => setDomain(null)} />
      <footer className="border-t border-line py-8">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-4 px-4 text-xs text-ink-3 sm:flex-row sm:items-center sm:px-6 lg:px-8">
          <Logo className="h-7" />
          <p>Educational content only. AI-generated material may contain errors — always verify against official guidance. No patient data is stored.</p>
        </div>
        <div className="mx-auto mt-4 max-w-7xl px-4 sm:px-6 lg:px-8"><LegalLinks /></div>
      </footer>
    </div>
  );
}
